import React, { useState, useRef, useEffect } from 'react';
import { Flex, ListItem, Box, Button } from '@opendoor/bricks/core';
import { css } from '@emotion/react';
import { colors } from '@opendoor/bricks/theme/eero';

/** Space (in pixels) between cards in teh carousel */
const GAP_BETWEEN_CARDS = 10;

interface CarouselProps {
  showMarketConditionCard?: boolean;
  /** Translate distance */
  width: number;
  /** Timer before slide switch in milliseconds */
  ms: number;
  /** Timing Function for animation transition between slides */
  transition: string;
  /** width to swipe before committing to full slide transition */
  snapAdjustment: number;
  children: React.ReactNode[];
  /** Which indicators to use. Defaults to bars */
  indicatorType?: 'pills' | 'bars';
  /** Whether to enable touch controls on mobile. Defaults to true */
  enableMobileTouch?: boolean;
}

/**
 * Entry point for the price adjustment experiment
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const AutoScrollCarousel: React.FC<CarouselProps> = ({
  width,
  ms,
  transition,
  snapAdjustment,
  children,
  indicatorType = 'bars',
  enableMobileTouch = true,
}) => {
  const [carouselPosition, setCarouselPosition] = useState(0);
  const [touchStartPosition, setTouchStartPosition] = useState(0);
  const [touchEndPosition, setTouchEndPosition] = useState(0);
  const [touched, setTouched] = useState(false);
  const [swiped, setSwiped] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setCarouselPosition((current) => (current < children.length - 1 ? current + 1 : 0));
    }, ms);
    return () => clearInterval(interval);
  });

  const prevHandler = () => {
    let position = carouselPosition;
    if (position > 0) position -= 1;
    fullTranslateSlide(position);
    setCarouselPosition(position);
  };

  const nextHandler = () => {
    let position = carouselPosition;
    if (position < children.length - 1) position += 1;
    fullTranslateSlide(position);
    setCarouselPosition(position);
  };

  /** fully snap to the next or prev slide */
  const fullTranslateSlide = (position: number) => {
    const translate = -width * position;
    if (boxRef.current) {
      boxRef.current.style.transform = `translateX(${translate}%) translateX(-${
        position * GAP_BETWEEN_CARDS
      }px)`;
    }
  };

  /** peek and slide through carousel */
  const touchWithMoveTranslate = (distance: number) => {
    if (boxRef.current) {
      const translate = -carouselPosition * width + distance;
      boxRef.current.style.transform = `translateX(${translate}%) translateX(-${
        carouselPosition * GAP_BETWEEN_CARDS
      }px)`;
    }
  };

  const touchStartHandler = (e: React.TouchEvent) => {
    setTouchStartPosition(e.targetTouches[0].clientX);
    setTouchEndPosition(e.targetTouches[0].clientX);
    setTouched(true);
  };

  /** handles movement between slides */
  const touchMoveHandler = (e: React.TouchEvent) => {
    if (boxRef.current) {
      setTouchEndPosition(e.targetTouches[0].clientX);
      const frameWidth = boxRef.current.offsetWidth;
      const translateDistance = ((touchEndPosition - touchStartPosition) / frameWidth) * width;
      touchWithMoveTranslate(translateDistance);
      if (touched) setSwiped(true);
    }
  };

  /** handles end of swipe and judges what the next slide will be */
  const touchEndHandler = () => {
    if (swiped && touchStartPosition - touchEndPosition > snapAdjustment) nextHandler();
    else if (swiped && touchStartPosition - touchEndPosition < -snapAdjustment) prevHandler();
    else {
      fullTranslateSlide(carouselPosition);
      setCarouselPosition(carouselPosition);
    }
    setTouched(false);
    setSwiped(false);
  };

  return (
    <Flex as="ul" flexDir="column" gridRowGap="4" overflowX="hidden">
      <Box
        transition={transition}
        display="flex"
        ref={boxRef}
        style={{
          transform: `translateX(${-carouselPosition * width}%) translateX(-${
            carouselPosition * GAP_BETWEEN_CARDS
          }px)`,
          touchAction: enableMobileTouch ? 'pan-x' : 'unset',
        }}
        gridGap={`${GAP_BETWEEN_CARDS}px`}
      >
        {children.map((item: React.ReactNode, i: number) => {
          return (
            <ListItem
              key={i}
              flexShrink={0}
              transition={transition}
              width="100%"
              onTouchStart={enableMobileTouch ? (e) => touchStartHandler(e) : undefined}
              onTouchMove={enableMobileTouch ? (e) => touchMoveHandler(e) : undefined}
              onTouchEnd={enableMobileTouch ? () => touchEndHandler() : undefined}
            >
              {item}
            </ListItem>
          );
        })}
      </Box>

      {indicatorType === 'bars' ? (
        <BarIndicators
          array={children}
          carouselPosition={carouselPosition}
          setCarouselPosition={setCarouselPosition}
          ms={ms}
        />
      ) : (
        <CircleIndicators
          array={children}
          carouselPosition={carouselPosition}
          setCarouselPosition={setCarouselPosition}
        />
      )}
    </Flex>
  );
};

interface CircleIndicatorsProps {
  array: React.ReactNode[];
  carouselPosition: number;
  setCarouselPosition: (value: number) => void;
}

const CircleIndicators: React.FC<CircleIndicatorsProps> = ({
  array,
  carouselPosition,
  setCarouselPosition,
}) => (
  <Flex as="div" flexDirection="row" justifyContent="center" gridGap="4">
    {array.map((_: React.ReactNode, index: number) => (
      <Button
        key={index}
        analyticsName="cake-auto-scroll-carousel-indicator"
        type="button"
        variant="icon"
        aria-label={`Show step ${index + 1} in the carousel`}
        onClick={() => setCarouselPosition(index)}
        width="8px"
        height="8px"
        bgColor={carouselPosition === index ? 'brand50' : 'neutrals40'}
        boxShadow="unset"
      />
    ))}
  </Flex>
);

interface BarIndicatorsProps {
  array: React.ReactNode[];
  carouselPosition: number;
  ms: number;
  setCarouselPosition: (value: number) => void;
}

const BarIndicators: React.FC<BarIndicatorsProps> = ({
  ms,
  array,
  carouselPosition,
  setCarouselPosition,
}) => {
  const totalNumOfIndicators = array.length;

  const indicatorAnimation = (index: number) => {
    const borderRadius =
      index === 0
        ? '10px 0px 0px 10px'
        : index + 1 === totalNumOfIndicators
        ? '0px 10px 10px 0px'
        : '0px';

    return css`
      padding: 0;
      min-height: 0;
      width: 53.5px;
      height: 3px;
      position: relative;
      background-color: #d9d9d9;
      border-radius: ${borderRadius};

      &:before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        width: ${index < carouselPosition ? '53.5px' : '0px'};
        height: 3px;
        background-color: ${colors.brand50};
        animation: ${carouselPosition === index ? `innerFill ${ms}ms forwards linear` : null};
        border-radius: ${borderRadius};
        @keyframes innerFill {
          from {
            width: 0px;
          }
          to {
            width: 53.5px;
          }
        }
      }
    `;
  };

  return (
    <Flex as="div" flexDirection="row" justifyContent="center" gridGap="4">
      {array.map((_: React.ReactNode, index: number) => (
        <Button
          key={index}
          analyticsName="cake-auto-scroll-carousel-indicator"
          type="button"
          aria-label={`Show step ${index + 1} in the carousel`}
          onClick={() => setCarouselPosition(index)}
          css={() => indicatorAnimation(index)}
        />
      ))}
    </Flex>
  );
};
